import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import {grey} from '@mui/material/colors'
import {useOnLoadGet} from 'app/hooks/common/useOnLoadGet'
import {ISubscription, ITenant} from 'app/models/tenant'
import {CSubscription, CTenant} from 'app/modules/discovery/core'
import {useMemo} from 'react'
import {PropertyEditor} from './usePropertyEditor'

export interface editSubscriptionProps {
  tenant: CTenant | null
  subscription: CSubscription | null
  close: () => void
}

export function DialogEditSubscription(props: {
  editor: PropertyEditor<ISubscription, {tenant: ITenant | null}>
}) {
  const packages = useOnLoadGet<any>(`std/package`, 'packages')
  const editor = props.editor

  const packageOptions = useMemo(
    () =>
      packages?.data?.map((x: any) => {
        return {label: x.name ?? '', id: x.id}
      }),
    [packages.dataUpdatedAt]
  )

  const selectedPackageId = editor.obj ? editor.getString('package_id') ?? null : null

  const selectedPackage = useMemo(() => {
    if (selectedPackageId === null) {
      return null
    }
    const matches = packageOptions?.filter((x: any) => x.id === selectedPackageId)
    if (!matches) {
      return null
    }
    return matches[0]
  }, [selectedPackageId])

  if (!editor.obj) {
    return <></>
  }
  if (!packageOptions) {
    return <></>
  }

  return (
    <Dialog open={true}>
      <DialogTitle sx={{backgroundColor: 'black', color: 'white'}}>
        {editor.createMode
          ? `Add Subscription to ${editor.related.tenant?.name}`
          : `Editing Subscription "${editor.obj.name}"`}
      </DialogTitle>
      <Box sx={{margin: '20px', minWidth: '500px', fontSize: '16px'}}>
        <Box sx={{marginBottom: '12px'}}>
          <form noValidate autoComplete='off'>
            <TextField
              sx={{marginBottom: '12px'}}
              id='name'
              label='Subscription name'
              value={editor.getString('name') ?? ''}
              onChange={(event: any) => {
                editor.setString('name', event.target.value)
              }}
              fullWidth
            />
            <Box sx={{display: 'flex', marginBottom: '12px'}}>
              <FormControlLabel
                sx={{flex: '0 0 auto', marginRight: '24px'}}
                label='Unlimited users'
                control={
                  <Switch
                    checked={!editor.getNumber('max_users')}
                    onChange={() => {
                      if (editor.getNumber('max_users')) {
                        editor.setNumber('max_users', null)
                      } else {
                        editor.setNumber('max_users', 10)
                      }
                    }}
                  />
                }
              ></FormControlLabel>
              <TextField
                sx={{
                  flex: '1 1 auto',
                  visibility: editor.getNumber('max_users') ? 'visible' : 'hidden',
                }}
                id='max_users'
                label='Maximum Users'
                type='number'
                value={editor.getNumber('max_users') ?? ''}
                onChange={(event: any) => {
                  editor.setNumber(
                    'max_users',
                    event.target.value ? parseInt(event.target.value) : undefined
                  )
                }}
                fullWidth
              />
            </Box>
            <Autocomplete
              disablePortal
              id='package-selector'
              options={packageOptions}
              getOptionLabel={(x) => x.label}
              value={selectedPackage}
              renderInput={(params) => <TextField {...params} label='Package'></TextField>}
              onChange={(event: any, val: {label: string; id: string | undefined} | null) => {
                editor.setString('package_id', val?.id)
              }}
            ></Autocomplete>
            <FormControlLabel
              control={
                <Checkbox
                  id={'allows_delegation'}
                  checked={editor.getBoolean('allows_delegation') ?? false}
                  onChange={(event: any) => {
                    editor.setBoolean('allows_delegation', event.target.value ? true : false)
                  }}
                />
              }
              label={
                <Typography variant={'h6'} color={grey[600]}>
                  Allow Delegation
                </Typography>
              }
            />
          </form>
        </Box>
        <Box sx={{display: 'flex'}}>
          <Box sx={{flex: '1 1 auto'}}></Box>
          <Box sx={{flex: '0 0 auto', display: 'flex'}}>
            <Button sx={{fontSize: '14px'}} onClick={editor.cancel}>
              Cancel
            </Button>
            <Button sx={{fontSize: '14px'}} onClick={editor.confirm}>
              OK
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  )
}
